import React, { FC, useEffect, useState } from 'react';

import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { ReactComponent as Cancel } from '../../../../assets/icons/cancel.svg';
import { ReactComponent as Check } from '../../../../assets/icons/v3/check-circle.svg';
import { ReactComponent as Upload } from '../../../../assets/icons/v3/circle-upload.svg';
import { UPLOAD_FILE_ERROR } from '../../../constants/toast';
import { IDropzoneValidator } from '../../../interfaces/fileUploaderContainer';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';
import { GenericObject, GenericVoidFunction } from '../../../types/genericTypes';
import { fileToBase64, isStringWithValue } from '../../../utils/utils';
import StyledCircularProgress from '../../StyledCircularProgress/StyledCircularProgress';
import Toast from '../Toast/Toast';

import './FileDropzone.scss';

interface IFileDropzoneProps {
  validator: IDropzoneValidator;
  updateBase64: GenericVoidFunction;
  selectedFile?: File;
  inputTitle?: string;
  externalError?: string;
  customData?: GenericObject;
  actionButton?: JSX.Element;
  saveFileName?: GenericVoidFunction;
  type?: 'file' | 'image';
  loading?: boolean;
  reset?: boolean;
  correctedFileMessage?: boolean;
}

const FileDropzone: FC<IFileDropzoneProps> = ({
  updateBase64,
  externalError,
  customData,
  validator,
  type = 'file',
  selectedFile,
  inputTitle,
  saveFileName,
  loading = false,
  actionButton,
  correctedFileMessage = false,
  reset
}) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>();
  const [file, setFile] = useState<File>();
  const [isToastOpen, setIsToastOpen] = useState(false);
  const { t } = useTranslation();

  const saveFile = async (file: File) => {
    updateBase64(await fileToBase64(file));
    setFile(file);
  };

  const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (acceptedFiles.length > 0) {
      if (file) deleteFile();
      saveFile(acceptedFiles[0]);
    }
    if (fileRejections.length > 0) {
      setIsToastOpen(true);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: validator.fileFormats as Accept,
    multiple: validator.maxFileCount > 1,
    maxFiles: validator.maxFileCount,
    maxSize: validator.maxFileSize,
    noDragEventsBubbling: true,
    onError: (error: Error) => {
      setError(t(error.message));
      setIsToastOpen(true);
    }
  });

  const getTitle = (): string => {
    if (inputTitle) return t(inputTitle);

    return type === 'file'
      ? t(TRANSLATIONS_GLOBAL.fileUploader.dragFile)
      : t(TRANSLATIONS_GLOBAL.fileUploader.dragImageNE);
  };

  const filesInfo = (): string => {
    if (customData?.prefixFileInfo) {
      return `${customData?.prefixFileInfo} / ${file!.name}`;
    }
    return file!.name;
  };

  const deleteFile = () => {
    setError(undefined);
    setFile(undefined);
    updateBase64(undefined);
  };

  const handleCloseToast = () => {
    setIsToastOpen(false);
  };

  useEffect(() => {
    setSuccess(!!file && !loading);
    if (!!file && saveFileName) {
      saveFileName(filesInfo());
    }
  }, [file, loading]);

  useEffect(() => {
    if (reset !== undefined) {
      deleteFile();
    }
  }, [reset]);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, [selectedFile]);

  return (
    <>
      <div
        className={`file-dropzone ${success ? 'upload-complete' : ''} ${isDragActive ? 'dragging' : ''}`}
        data-experience="new"
      >
        {success ? (
          <div className={`file-uploaded ${error ? 'error' : 'success'}`}>
            {
              <div className="flex flex-column items-center justify-center w-full p-6">
                <div className="flex flex-column items-center justify-center w-full gap-4">
                  <div className="flex flex-column items-center justify-center w-full gap-7">
                    {!isStringWithValue(error) ? <Check /> : <Cancel />}
                    <div className="dropzone-file-info">{file && filesInfo()}</div>
                    {isStringWithValue(error) && (
                      <div className="flex flex-column items-start justify-center">
                        <div className="dropzone-error-text" key={`error${uuid()}`}>
                          {error}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex align-center justify-between gap-4 whitespace-nowrap">
                    {actionButton && <div className="flex justify-center">{actionButton}</div>}
                    <div className="flex mt-2 items-center">
                      {correctedFileMessage && (
                        <span className="dropzone-corrected-title underline cursor-pointer" {...getRootProps()}>
                          {t(TRANSLATIONS_GLOBAL.fileDropzone.correctedFile)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        ) : (
          <div
            {...getRootProps()}
            className="dropzone flex flex-column items-center justify-center p-12 w-full text-center"
          >
            {loading ? (
              <div>
                <div className="w-full px-3">
                  <StyledCircularProgress size={32} />
                </div>
                <div className="flex flex-column items-center gap-2">
                  <p className="dropzone-loading-title m-0">{t(TRANSLATIONS_GLOBAL.fileDropzone.loading.title)}</p>
                </div>
              </div>
            ) : (
              <>
                <input {...getInputProps()} data-testid="dropzone" />
                <Upload style={{ width: '60px', height: '60px' }} />
                <div className="dropzone-upload-title mt-7 mb-4">{getTitle()}</div>
                <span className="dropzone-format-description">
                  {t(TRANSLATIONS_GLOBAL.fileDropzone.formatDescription)}
                </span>
              </>
            )}
          </div>
        )}
      </div>
      <Toast
        title={t(UPLOAD_FILE_ERROR.title)}
        description={t(UPLOAD_FILE_ERROR.description)}
        icon={UPLOAD_FILE_ERROR.icon}
        open={isToastOpen}
        button={UPLOAD_FILE_ERROR.button}
        buttonText={t(UPLOAD_FILE_ERROR.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default FileDropzone;
